import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Sparkles } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../context/ProductsContext';

const Home = () => {
  const { products } = useProducts();
  const deals = products.filter((p) => p.discount > 0);
  const watches = products.filter((p) => p.category === 'ceasuri');
  const featuredProducts = products.slice(0, 8);

  return (
    <div className="bg-white">
      {/* HERO — Big, clean, single focus */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-100 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-medium text-amber-800 uppercase tracking-wider">Colecția 2026</span>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-neutral-900 leading-[1.05] tracking-tight">
              Genți din<br />piele naturală
            </h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-md leading-relaxed">
              Calitate care se vede. Stil care rezistă. Selecție premium de genți, serviete, rucsacuri și ceasuri.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/categorie/genti"
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white px-7 py-3.5 rounded-full text-sm font-medium transition-colors"
              >
                Vezi colecția
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/categorie/ceasuri"
                className="inline-flex items-center gap-2 bg-white text-neutral-900 border border-neutral-300 hover:border-neutral-900 px-7 py-3.5 rounded-full text-sm font-medium transition-colors"
              >
                Ceasuri
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-6 border-t border-neutral-200 pt-8 max-w-md">
              <div>
                <div className="text-2xl font-serif font-bold text-neutral-900">15+</div>
                <div className="text-xs text-neutral-500 mt-0.5">Produse premium</div>
              </div>
              <div>
                <div className="text-2xl font-serif font-bold text-neutral-900">24h</div>
                <div className="text-xs text-neutral-500 mt-0.5">Livrare rapidă</div>
              </div>
              <div>
                <div className="text-2xl font-serif font-bold text-neutral-900">14 zile</div>
                <div className="text-xs text-neutral-500 mt-0.5">Drept de retur</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-neutral-50">
              <img
                src="https://images.unsplash.com/photo-1691480150204-66dd1eb77391?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="Geantă business din piele naturală"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating price badge */}
            <Link
              to="/produs/geanta-laptop-14-inch-piele-verde-eleganta"
              className="absolute bottom-6 left-6 right-6 sm:left-auto sm:right-6 sm:max-w-xs bg-white rounded-2xl shadow-xl border border-neutral-100 p-5 hover:shadow-2xl hover:-translate-y-0.5 transition-all"
            >
              <div className="text-xs uppercase tracking-wider text-amber-700 font-medium">Cel mai vândut</div>
              <div className="mt-1 font-medium text-neutral-900">Geantă business laptop 14”</div>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-2xl font-serif font-bold text-neutral-900">785 lei</span>
                <span className="text-sm text-neutral-400 line-through pb-0.5">1.157 lei</span>
              </div>
              <span className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">
                -32%
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-neutral-50 border-y border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Truck, t: 'Livrare 24-48h', s: 'Curier rapid în toată țara' },
            { icon: ShieldCheck, t: 'Garanție 24 luni', s: 'Pentru toate produsele' },
            { icon: RotateCcw, t: 'Retur 14 zile', s: 'Returnare simplă' },
            { icon: Sparkles, t: 'Calitate premium', s: 'Piele naturală selectată' },
          ].map((f) => (
            <div key={f.t} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0">
                <f.icon className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-neutral-900">{f.t}</div>
                <div className="text-xs text-neutral-500">{f.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories — clean text buttons */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-amber-700 font-medium">Categorii</span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-neutral-900 mt-3">
            Cumpără după categorie
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/categorie/genti"
            className="group relative overflow-hidden rounded-3xl bg-neutral-100 aspect-[4/5] md:aspect-auto md:row-span-2 flex items-end"
          >
            <img
              src="https://images.unsplash.com/photo-1622560480605-d83c853bc5c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Genți"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="relative p-8 text-white">
              <div className="text-xs uppercase tracking-[0.2em] opacity-80">12 produse</div>
              <h3 className="font-serif text-4xl md:text-5xl font-bold mt-2">Genți</h3>
              <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium">
                Vezi toate <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          <Link
            to="/categorie/ceasuri"
            className="group relative overflow-hidden rounded-3xl bg-neutral-100 aspect-[4/3] flex items-end"
          >
            <img
              src="https://images.unsplash.com/photo-1600003014755-ba31aa59c4b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Ceasuri"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="relative p-7 text-white">
              <div className="text-xs uppercase tracking-[0.2em] opacity-80">3 produse</div>
              <h3 className="font-serif text-3xl font-bold mt-1">Ceasuri</h3>
              <span className="inline-flex items-center gap-1 mt-2 text-sm font-medium">
                Vezi toate <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          <Link
            to="/categorie/articole-pentru-bucatarie"
            className="group relative overflow-hidden rounded-3xl bg-amber-50 border border-amber-100 aspect-[4/3] flex flex-col justify-between p-7"
          >
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-amber-700">Nou în colecție</div>
              <h3 className="font-serif text-3xl font-bold text-neutral-900 mt-1">Bucătărie</h3>
            </div>
            <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-800">
              Vezi produse <ArrowRight className="w-4 h-4" />
            </span>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full bg-amber-200/40" />
          </Link>
        </div>
      </section>

      {/* Bestsellers */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-16 md:pb-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-amber-700 font-medium">Selecția noastră</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-neutral-900 mt-2">
              Cele mai populare
            </h2>
          </div>
          <Link to="/magazin" className="inline-flex items-center gap-1 text-sm font-medium text-neutral-900 hover:text-amber-700 transition-colors">
            Vezi toate produsele <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
          {featuredProducts.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Sale Banner */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-16 md:pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-neutral-900 grid grid-cols-1 lg:grid-cols-2">
          <div className="p-10 md:p-16 flex flex-col justify-center">
            <span className="text-xs uppercase tracking-[0.3em] text-amber-400 font-medium">Reducere limitată</span>
            <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 leading-[1.05]">
              Până la <span className="text-amber-400">-32%</span><br />
              la genți premium
            </h3>
            <p className="mt-5 text-neutral-300 max-w-md">
              Selecție specială de genți business și rucsacuri din piele naturală, la prețuri reduse.
            </p>
            <Link
              to="/magazin"
              className="mt-8 inline-flex w-fit items-center gap-2 bg-white hover:bg-amber-400 text-neutral-900 px-7 py-3.5 rounded-full text-sm font-medium transition-colors"
            >
              Cumpără acum <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative min-h-[280px] lg:min-h-[400px]">
            <img
              src="https://images.unsplash.com/photo-1622560480654-d96214fdc887?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Rucsac piele"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Deals + Watches split */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-neutral-900">Oferte speciale</h2>
            <Link to="/magazin" className="text-sm font-medium text-neutral-600 hover:text-amber-700">
              Toate ofertele →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {deals.concat(products.slice(6, 8)).slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-neutral-900 mb-8">Ceasuri</h2>
          <div className="space-y-4">
            {watches.map((p) => (
              <Link
                key={p.id}
                to={`/produs/${p.slug}`}
                className="flex items-center gap-4 p-3 -mx-3 rounded-2xl hover:bg-neutral-50 transition-colors group"
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-20 h-20 rounded-xl object-cover bg-neutral-50 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-neutral-900 line-clamp-2 group-hover:text-amber-700 transition-colors">
                    {p.name}
                  </h4>
                  <div className="mt-1 font-serif font-bold text-neutral-900">
                    {p.price.toLocaleString('ro-RO')} lei
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
          <Link
            to="/categorie/ceasuri"
            className="mt-6 block w-full text-center border border-neutral-300 hover:border-neutral-900 text-neutral-900 text-sm font-medium py-3 rounded-full transition-colors"
          >
            Vezi toate ceasurile
          </Link>
        </div>
      </section>

      {/* About */}
      <section className="bg-neutral-50 border-t border-neutral-100">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <span className="text-xs uppercase tracking-[0.25em] text-amber-700 font-medium">Despre AXA</span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-neutral-900 mt-3">
            Genți, ceasuri și accesorii pentru un stil complet
          </h2>
          <p className="mt-6 text-neutral-600 leading-relaxed text-lg">
            La AXA am selectat produse care îți fac viața mai simplă și stilul mai clar.
            Găsești într-un singur loc lucruri care arată bine, sunt practice și merită banii.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-neutral-900 border-b border-neutral-900 pb-1 hover:text-amber-700 hover:border-amber-700 transition-colors"
          >
            Ia legătura cu noi
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
